import { Box, Button, Text, Popover, Select, Space, Group, Menu, CloseButton, ThemeIcon, Loader, Tooltip } from '@mantine/core';
import { useDi, useDiMemo } from '@root/Services/DI';
import { FormatService } from '@root/Services/FormatService';
import { InvoiceApiService } from '@root/Services/Invoices/InvoiceApiService';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Calendar, ChevronDown, ChevronUp, BookUpload } from 'tabler-icons-react';
import { useDisclosure } from '@mantine/hooks';
import { DataGrid, GridCurrencyCell } from '@root/Components/DataGrid';
import { ColumnConfig } from '@root/Components/DataGrid/Models';
import { IncreaseIndicator } from '@root/Design/Data';
import { Picker } from '@root/Components/Picker/Picker';
import { getUniqueMonths, useRangePickerData } from '@root/Site/Invoices/Components/MonthlyRangePicker';
import { BillingInvoice, BillingInvoiceAdjustment, PostBillingInvoiceSyncQuickbooksInvoiceParams } from '@apis/Invoices/model';
import { postResaleCostQuery, postBillingInvoiceSyncQuickbooksInvoice, getBillingInvoiceGetBillingInvoicesForCompany } from '@apis/Invoices';
import { queryBuilder, ValuesGroupOtherText } from '@root/Services/QueryExpr';
import { getAccountGetCompanyAccounts, getCompanyGetCompanyCloudProviders, getCompanyGetCompanyConfig } from '@apis/Customers';
import { getDaysInMonth } from 'date-fns';
import { CompanyContextService } from '@root/Services/Customers/CompanyContext';
import { useCompany } from '../Router/CompanyContent';
import { NotificationService } from '@root/Services/Notification/NotificationService';
import { Account } from '@apis/Customers/model';
import { FillerSwitch } from '@root/Design/Filler';
import { EmptyDataText } from '../Text/VisibleSpaces';
import { useQuickbooksAuthenticator } from '../Connections/QbConnector';
import { ActivityPanelModel } from '../Actions/ActivityPanel/Models';
import { JobService } from '@root/Services/Jobs/JobService';
import { Platform } from '@root/Services/PlatformService';

export interface CostData {
    ProductName: string;
    Cost: number;
    ServiceCategory?: string;
}

export interface ResellCostComparisonRow {
    ProductName: string;
    UfProductName: string;
    EmptyProductName: boolean;
    ServiceCategory?: string;
    ResaleCost: number;
    CurCost: number;
    DifferenceDollars: number;
    DifferencePercent: number;
}

export function ResellCostComparison(props: {
    onClose: () => void;
    initialMonth?: Date;
    invoiceId: number;
    onAdjustmentAdded: (adjustment: BillingInvoiceAdjustment) => void;
}) {
    const fmtSvc = useDi(FormatService);
    const invoiceApi = useDiMemo(InvoiceApiService);
    const [loading, setLoading] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState<Date>(props.initialMonth || new Date());
    const [selectedCloudProvider, setSelectedCloudProvider] = useState<string>();
    const platform: Platform = selectedCloudProvider === 'AWS' ? 'Aws' : 'Azure';
    const [comparisonData, setComparisonData] = useState<{ totals?: ResellCostComparisonRow; rows: ResellCostComparisonRow[] }>({ rows: [] });
    const [monthPickerOpened, { toggle: toggleMonthPicker, close: closeMonthPicker }] = useDisclosure(false);

    const { data: ranges, loading: rangesLoading } = useRangePickerData(invoiceApi);
    const availableMonths = useMemo(() => (ranges ? getUniqueMonths(ranges, fmtSvc).map((r) => r.date) : []), [ranges]);
    const [cloudProviders, setCloudProviders] = useState<{ value: string; label: string }[]>();
    const companyCtx = useDi(CompanyContextService);
    const company = useCompany();
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [allInvoices, setAllInvoices] = useState<BillingInvoice[]>([]);

    useEffect(() => {
        (async () => {
            const invoices = await getBillingInvoiceGetBillingInvoicesForCompany({ companyId: company?.Id! });
            setAllInvoices(invoices ?? []);
        })();
    }, []);

    const currentInvoiceId = useMemo(() => {
        const year = selectedMonth.getFullYear();
        const month = selectedMonth.getMonth() + 1;
        return allInvoices.find((inv) => inv.Year === year && inv.Month === month)?.Id ?? props.invoiceId;
    }, [selectedMonth, allInvoices, props.invoiceId]);

    useEffect(() => {
        (async () => {
            const providers = await getCompanyGetCompanyCloudProviders();
            const uniqueProviders = [...new Set(providers.map((p) => p.CloudPlatform!))].map((platform) => ({
                value: platform === 'Aws' ? 'AWS' : platform,
                label: platform,
            }));
            setCloudProviders(uniqueProviders);
            setSelectedCloudProvider(uniqueProviders[0]?.value);
        })();
    }, []);

    const MonthPicker = ({ month, onChange, months }: { month: Date; onChange: (value: Date) => void; months: Date[] }) => (
        <Popover opened={monthPickerOpened} onClose={closeMonthPicker} position="bottom-start" withArrow withinPortal offset={0}>
            <Popover.Target>
                <Button
                    variant="outline"
                    leftIcon={<Calendar size={16} />}
                    rightIcon={monthPickerOpened ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    onClick={toggleMonthPicker}
                >
                    {fmtSvc.formatLongMonthYear(month)}
                </Button>
            </Popover.Target>
            <Popover.Dropdown p={0}>
                <Picker
                    items={months}
                    onChange={(selected: Date[]) => {
                        if (selected[0]) {
                            onChange(selected[0]);
                            closeMonthPicker();
                        }
                    }}
                    selections={[month]}
                    nameAccessor={(m) => fmtSvc.formatLongMonthYear(m)}
                    minimizeHeight
                    height={300}
                    width={200}
                    mode="single"
                    noFilter
                />
            </Popover.Dropdown>
        </Popover>
    );

    const CloudProviderPicker = ({ selectedProvider, onChange }: { selectedProvider: string; onChange: (value: string) => void }) => (
        <Select
            data={cloudProviders || []}
            value={selectedProvider}
            onChange={(value) => onChange(value!)}
            placeholder="Select cloud provider"
            style={{ width: 150 }}
        />
    );

    const fetchData = useCallback(
        async (selectedMonth: Date, selectedCloudProvider: string) => {
            try {
                setLoading(true);

                const accounts = await getAccountGetCompanyAccounts();
                setAccounts(accounts);

                type CostRecord = {
                    BilledCost: number;
                    ProviderName: string;
                    ProductName: string;
                    MeterCategory: string;
                    ServiceCategory: string;
                    'product/ProductName': string;
                };

                const resaleResultsReq = queryBuilder<CostRecord>()
                    .where((b) => b.model.BilledCost.ne(b.param(0)))
                    .select((b) => ({
                        ProductName: b.values(b.model[selectedCloudProvider === 'AWS' ? 'ProductName' : 'MeterCategory'], null, ValuesGroupOtherText),
                        Cost: b.sum(b.model.BilledCost),
                    }))
                    .execute((q) =>
                        postResaleCostQuery(
                            q,
                            {
                                months: [fmtSvc.formatYearMonth(selectedMonth)],
                                platforms: [selectedCloudProvider as 'Aws' | 'Azure'],
                                companyId: company?.Id,
                            },
                            companyCtx.getRequestOptions(true)
                        )
                    );

                const curResultsReq = queryBuilder<CostRecord>()
                    .where((b) =>
                        b.and(b.model.BilledCost.ne(b.param(0)), b.model.ProviderName.eq(selectedCloudProvider === 'AWS' ? 'AWS' : 'Azure'))
                    )
                    .select((b) => ({
                        ProductName: b.values(
                            selectedCloudProvider === 'AWS' ? b.model['product/ProductName'] : b.model.MeterCategory,
                            null,
                            ValuesGroupOtherText
                        ),
                        Cost: b.sum(b.model.BilledCost),
                        ServiceCategory: b.values(b.model.ServiceCategory, null, ValuesGroupOtherText),
                    }))
                    .execute((q) => invoiceApi.queryMonthlyRollup(q, [selectedMonth]));

                const [resaleResults, curResults] = await Promise.all([resaleResultsReq, curResultsReq]);
                return [resaleResults?.Results ?? [], curResults?.Results ?? []] as const;
            } finally {
                setLoading(false);
            }
        },
        [invoiceApi]
    );

    const updateComparisonRowData = useCallback(
        async (selectedMonth: Date, selectedCloudProvider: string) => {
            if (!selectedMonth || !selectedCloudProvider) {
                return;
            }

            const [resaleCostData, curCostData] = await fetchData(selectedMonth, selectedCloudProvider);

            const allProductNames = new Set([...resaleCostData.map((item) => item.ProductName), ...curCostData.map((item) => item.ProductName)]);

            let totalResale = 0;
            let totalCur = 0;
            const transformedData: ResellCostComparisonRow[] = [...allProductNames].map((productName) => {
                const resaleCost = resaleCostData.reduce((acc, item) => acc + (item.ProductName === productName ? item.Cost : 0), 0);
                const curCost = curCostData.reduce((acc, item) => acc + (item.ProductName === productName ? item.Cost : 0), 0);
                const curRow = curCostData.find((item) => item.ProductName === productName);

                totalResale += resaleCost;
                totalCur += curCost;

                const productNameEmpty = !productName || productName === ValuesGroupOtherText;

                return {
                    ProductName: productName,
                    UfProductName:
                        productName === ValuesGroupOtherText ? '« No Product Name »' : !productName ? '« Blank Product Name »' : productName,
                    EmptyProductName: productNameEmpty,
                    ResaleCost: resaleCost,
                    CurCost: curCost,
                    DifferenceDollars: resaleCost - curCost,
                    DifferencePercent: curCost === 0 ? 0 : (resaleCost / curCost) * 100,
                    ServiceCategory: curRow?.ServiceCategory || '',
                };
            });

            setComparisonData({
                totals: {
                    ProductName: '',
                    UfProductName: '',
                    EmptyProductName: false,
                    CurCost: totalCur,
                    ResaleCost: totalResale,
                    DifferenceDollars: totalResale - totalCur,
                    DifferencePercent: totalCur === 0 ? 0 : (totalResale / totalCur) * 100,
                },
                rows: transformedData,
            });
        },
        [fetchData]
    );

    useEffect(() => {
        if (selectedMonth && selectedCloudProvider) {
            updateComparisonRowData(selectedMonth, selectedCloudProvider);
        } else {
            setComparisonData({ rows: [] });
        }
    }, [updateComparisonRowData, selectedMonth, selectedCloudProvider]);

    const isAdjustmentEnabled = (row: ResellCostComparisonRow) => {
        return row.DifferenceDollars !== 0 && row.CurCost !== 0;
    };

    const handleAddPercentBasedAdjustment = (row: ResellCostComparisonRow) => {
        const percentageDifference = row.DifferencePercent;
        const adjustmentOperation = 'ExistingPercent';
        const providerProductField = selectedCloudProvider === 'AWS' ? 'AWS.product/ProductName' : 'Azure.MeterCategory';
        // Create the adjustment data
        const adjustmentData: BillingInvoiceAdjustment = {
            AdjustmentOperation: adjustmentOperation,
            Amount: percentageDifference,
            Description: `(Added via Comparison) Adjust ${row.ProductName} by ${fmtSvc.formatDecimal(percentageDifference, 2)}% to match CSP cost`,
            ApplyTo: {
                InclusionRules: [
                    {
                        Name: 'ProductName',
                        Filter: {
                            Operation: 'and',
                            Operands: [
                                row.EmptyProductName
                                    ? {
                                          Operation: 'isNull',
                                          Operands: [{ Field: providerProductField }],
                                      }
                                    : {
                                          Operation: 'eq',
                                          Operands: [
                                              {
                                                  Field: providerProductField,
                                              },
                                              {
                                                  Value: [row.ProductName],
                                              },
                                          ],
                                      },
                            ],
                        },
                    },
                ],
                ExclusionRules: [],
            },
            SourceKey: `${adjustmentOperation}.${row.ProductName}`,
            Platform: selectedCloudProvider === 'AWS' ? 'Aws' : 'Azure',
            BillingInvoiceId: props.invoiceId,
            Order: 0,
        };

        props.onAdjustmentAdded(adjustmentData);
    };

    const handleAddPriorPeriodAdjustment = (row: ResellCostComparisonRow) => {
        const lastDayOfMonth = getDaysInMonth(selectedMonth);

        // Find the master/management account
        const masterAccount = accounts.find((account) => account.IsMasterAccount === true);

        const billingAccountId = masterAccount?.AwsAccountId || '';
        const billingAccountName = masterAccount?.Name || '';
        const varianceKey = `Anniversary/Usage/Adjustment/Dollar`;

        const customLineItemData = {
            AvailabilityZone: '',
            'bill/BillingEntity': 'AWS', // Default to AWS; modify if Azure is added
            'bill/BillType': '',
            'bill/InvoiceId': '',
            BilledCost: row.DifferenceDollars,
            BilledDate: lastDayOfMonth.toString(),
            BillingAccountId: billingAccountId,
            BillingAccountName: billingAccountName,
            BillingCurrency: 'USD',
            ChargeCategory: 'Usage',
            ChargeDescription: 'Prior period adjustment',
            ChargeFrequency: 'One-Time', // Assuming one-time for all prior period adjustments
            ConsumedUnit: 'Dollar',
            CspSchemaVersion: 'CUR 1.0',
            EffectiveCost: row.DifferenceDollars,
            FocusSchemaVersion: 'v1.0',
            InvoiceIssuerName: 'Amazon Web Services, Inc.',
            'lineItem/LineItemType': 'Usage',
            'lineItem/Operation': '',
            'lineItem/UsageType': '',
            ListCost: row.DifferenceDollars,
            ListUnitPrice: row.DifferenceDollars,
            NativeLineItemType: 'Usage',
            NativeUsageType: '',
            'pricing/publicOnDemandCost': row.DifferenceDollars,
            'pricing/term': '',
            PricingQuantity: 1,
            PricingUnit: 'Dollar',
            'product/productFamily': '',
            'product/ProductName': row.ProductName,
            'product/servicecode': '',
            'product/servicename': '',
            ProviderName: 'AWS', // Default to AWS; modify if Azure is added
            RegionId: '',
            RegionName: '',
            ServiceCategory: row.ServiceCategory || '',
            ServiceName: row.ProductName,
            SubAccountId: billingAccountId,
            SubAccountName: billingAccountName,
            VarianceKey: varianceKey,
        };

        const adjustmentData: BillingInvoiceAdjustment = {
            AdjustmentOperation: 'CustomLineItems',
            Amount: 0,
            Description: `(Added via Comparison) Prior period adjustment`,
            SourceKey: 'PriorPeriodAdjustments:All',
            Platform: selectedCloudProvider === 'AWS' ? 'Aws' : 'Azure',
            BillingInvoiceId: props.invoiceId,
            ImportFields: JSON.stringify([customLineItemData]),
            Order: 0,
        };

        props.onAdjustmentAdded(adjustmentData);
    };

    const ProductNameCellRenderer = ({ row }: { row: ResellCostComparisonRow }) => {
        const [menuOpened, setMenuOpened] = useState(false);

        return (
            <Menu opened={menuOpened} onClose={() => setMenuOpened(false)} position="bottom-start" withinPortal>
                <Menu.Target>
                    <div style={{ cursor: 'pointer' }} onClick={() => setMenuOpened(true)}>
                        {row.EmptyProductName ? <EmptyDataText text={row.UfProductName} /> : row.UfProductName}
                    </div>
                </Menu.Target>
                <Menu.Dropdown>
                    <Menu.Item
                        disabled={!isAdjustmentEnabled(row)}
                        onClick={() => {
                            handleAddPercentBasedAdjustment(row);
                            setMenuOpened(false);
                        }}
                    >
                        Add % based Adjustment
                    </Menu.Item>
                    <Menu.Item
                        disabled={row.DifferenceDollars === 0}
                        onClick={() => {
                            handleAddPriorPeriodAdjustment(row);
                            setMenuOpened(false);
                        }}
                    >
                        Add Prior Period Adjustment
                    </Menu.Item>
                </Menu.Dropdown>
            </Menu>
        );
    };

    // Create columns directly without useMemo to ensure footer renderers get fresh values
    const columns: ColumnConfig<ResellCostComparisonRow>[] = [
        {
            accessor: 'ProductName',
            defaultWidth: 350,
            id: 'ProductName',
            header: 'Product Name',
            type: 'string',
            defaultFixed: true,
            footerRenderer: () => 'Total',
            cellRenderer: (row) => <ProductNameCellRenderer row={row} />,
        },
        {
            accessor: 'ResaleCost',
            defaultWidth: 200,
            id: 'ResaleCost',
            header: 'TDS Cost',
            type: 'number',
            align: 'right',
            cellRenderer: (row) => <GridCurrencyCell value={row.ResaleCost} moneyFormat="number-with-six-decimals" />,
            footerRenderer: () => <GridCurrencyCell value={comparisonData.totals?.ResaleCost} moneyFormat="number-with-six-decimals" />,
        },
        {
            accessor: 'CurCost',
            defaultWidth: 200,
            id: 'CurCost',
            header: 'CS Cost',
            type: 'number',
            align: 'right',
            cellRenderer: (row) => <GridCurrencyCell value={row.CurCost} moneyFormat="number-with-six-decimals" />,
            footerRenderer: () => <GridCurrencyCell value={comparisonData.totals?.CurCost} moneyFormat="number-with-six-decimals" />,
        },
        {
            accessor: 'DifferenceDollars',
            defaultWidth: 200,
            id: 'DifferenceDollars',
            header: 'Difference ($)',
            type: 'number',
            align: 'right',
            cellRenderer: (item: ResellCostComparisonRow) => {
                return (
                    <Group noWrap position="apart" className="currency-cell">
                        <span>$</span>
                        <span>
                            {fmtSvc.format('number-with-six-decimals', item.DifferenceDollars)}
                            <IncreaseIndicator value={item.DifferenceDollars} preferDecrease size="sm" />
                        </span>
                    </Group>
                );
            },
            footerRenderer: () => {
                const total = comparisonData.totals?.DifferenceDollars || 0;
                return (
                    <Group noWrap position="apart" className="currency-cell">
                        <span>$</span>
                        <span>
                            {total ? fmtSvc.format('number-with-six-decimals', total) : ''}
                            <IncreaseIndicator value={total} preferDecrease size="sm" />
                        </span>
                    </Group>
                );
            },
        },
        {
            accessor: 'DifferencePercent',
            defaultWidth: 200,
            id: 'DifferencePercent',
            header: 'Difference (%)',
            type: 'number',
            align: 'right',
            formatter: (row) => (!row.DifferencePercent ? '-' : `${fmtSvc.formatDecimal(row.DifferencePercent, 6)}%`),
            footerRenderer: () => `${fmtSvc.formatDecimal(comparisonData.totals?.DifferencePercent ?? 0, 6)}%`,
        },
    ];

    return (
        <Box sx={{ height: 'calc(100vh - 200px)' }}>
            <FillerSwitch loading={loading}>
                {() => (
                    <>
                        <DataGrid
                            columns={columns}
                            dataSource={comparisonData.rows}
                            statePersistence={{ key: 'resell-cost-comparison' }}
                            hideGlobalSearch
                            hideColumnSelector
                            hideFilterOnly
                            leftTopPlaceHolder={
                                <Text pl="md" pb={4} size="lg" color="dimmed">
                                    Resell Cost Comparison
                                </Text>
                            }
                            rightToolPlaceHolder={
                                <>
                                    <SyncToQuickbooksButton platform={platform} invoiceId={currentInvoiceId} />
                                    <Space w={10} />
                                    <CloudProviderPicker selectedProvider={selectedCloudProvider!} onChange={setSelectedCloudProvider} />
                                    <Space w={10} />
                                    <MonthPicker month={selectedMonth} onChange={setSelectedMonth} months={availableMonths} />
                                    <Space w={20} />
                                    <CloseButton onClick={props.onClose} />
                                </>
                            }
                            onRowClick={() => {}}
                            rowStyle={() => ({ cursor: 'auto' })}
                            footerPosition="top"
                            renderFooter
                            exportName="Resell Cost Comparison"
                        />
                    </>
                )}
            </FillerSwitch>
        </Box>
    );
}

function SyncToQuickbooksButton(props: { platform: Platform; invoiceId: number }) {
    const notificationSvc = useDi(NotificationService);
    const activityPanelModel = useDi(ActivityPanelModel);
    const jobSvc = useDi(JobService);
    const companyCtx = useDi(CompanyContextService);

    const { platform, invoiceId } = props;
    const qbAuthenticator = useQuickbooksAuthenticator();
    const [hasQbId, setHasQbId] = useState<boolean>();
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        let disposed = false;
        (async () => {
            const config = await getCompanyGetCompanyConfig();
            if (!disposed) {
                setHasQbId(config.some((x) => x.Key === 'QuickbooksId' && x.Value));
            }
        })();

        return () => {
            disposed = true;
        };
    }, [invoiceId]);

    const syncToQuickbooks = async () => {
        setLoading(true);
        try {
            const options = companyCtx.getRequestOptions(true);

            await qbAuthenticator();
            const params = {
                invoiceId: invoiceId,
                platform: platform,
            } as PostBillingInvoiceSyncQuickbooksInvoiceParams;

            const job = await postBillingInvoiceSyncQuickbooksInvoice(params, options);

            companyCtx.withParentCompany(() => {
                notificationSvc.notify(
                    'Quickbooks Sync',
                    `Quickbooks sync started. Please check the activity log for status.`,
                    'primary',
                    <ThemeIcon variant="light" size="xl" radius="xl">
                        <BookUpload />
                    </ThemeIcon>
                );
                jobSvc.notifyNewJob(job);
            });
        } catch (err: any) {
            notificationSvc.notify('Failed to sync to Quickbooks. Refresh browser and retry.', err?.message ?? '', 'error', null);
        } finally {
            setLoading(false);
        }
    };

    const icon = loading || hasQbId === undefined ? <Loader size="sm" /> : <BookUpload size={16} />;
    const tooltip = hasQbId !== false ? '' : 'No Quickbooks ID configured for this customer.';

    return (
        <Tooltip label={tooltip} disabled={!tooltip}>
            <Button variant="outline" leftIcon={icon} onClick={syncToQuickbooks} disabled={loading || !hasQbId}>
                Sync to Quickbooks
            </Button>
        </Tooltip>
    );
}
